import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  GenericIconName,
  Icon,
  RADIUS,
  SPACING,
  Text,
  UnstyledButton,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {
  MissionsSectionHeader,
  MissionsSectionLayout,
  MissionsSectionProps,
  MissionTile,
  useMissionsSection,
} from './MissionsSection';

const COLUMNS = 3;

/**
 * Browse missions as a row of up to three cards with a "See all Missions"
 * pill in the header. Used by the desktop app.
 */
export const MissionsSectionCards: React.FC<MissionsSectionProps> = ({
  headline,
  subline,
  missions,
  promotionTracker,
  streamPosition,
}) => {
  const {t} = useTranslation();
  const {visibleMissions, navigateToMission, openCoinsWallet} =
    useMissionsSection({missions, promotionTracker});

  if (visibleMissions.length === 0) {
    return null;
  }

  return (
    <MissionsSectionLayout
      promotionTracker={promotionTracker}
      streamPosition={streamPosition}
    >
      <MissionsSectionHeader headline={headline} subline={subline}>
        <WalletCta
          data-testid="missionsSectionWalletLink"
          type="button"
          aria-label={undefined}
          onClick={openCoinsWallet}
        >
          <Text size="px10" weight="w500">
            {t('See all Missions')}
          </Text>
          <Icon asset={GenericIconName.ArrowLongRight} size="SS" />
        </WalletCta>
      </MissionsSectionHeader>
      <Cards>
        {visibleMissions.map(mission => (
          <Card key={mission.missionIdentifier.missionId}>
            <MissionTile mission={mission} onNavigate={navigateToMission} />
          </Card>
        ))}
      </Cards>
    </MissionsSectionLayout>
  );
};

const WalletCta = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 36px;
  padding-left: ${SPACING['X.Lg']};
  padding-right: ${SPACING['X.Sm']};
  border: 1px solid ${COLORS['Outline.Grey']};
  border-radius: ${RADIUS.Round};
  background-color: rgba(255, 255, 255, 0.4);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);

  > span {
    white-space: nowrap;
    padding-right: ${SPACING['X.Sm']};
  }

  > svg {
    margin: 6px;
  }
`;

/* Fixed columns: a card keeps a third of the row even when fewer than three
   missions arrive, and every card stretches to the tallest one. */
const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(${COLUMNS}, minmax(0, 1fr));
  align-items: stretch;
  gap: ${SPACING['X.Md']};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-inline: 12px;
  border: 1px solid ${COLORS['Outline.Grey']};
  border-radius: ${RADIUS.Sm};
`;
